import React, { useEffect } from 'react';

import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useChannelAdminContext } from 'hooks';
import { PERMISSION_KEYS } from 'constants-permissions';

import { useUserDataContext } from 'lane-shared/hooks';
import { Channel } from 'lane-shared/types/ChannelType';
import { UserType } from 'lane-shared/types/User';
import { hasPermission } from 'lane-shared/helpers';

import { Loading } from 'components/general';
import { AdminPage } from 'components/layout';

import { useReport } from './hooks/useReport';
import { getTimeZone } from './utilities';
import { getReportIds } from './constants';
import styles from './Report.scss';
import { Alert, BreadCrumbs } from 'components/lds';
import { routes } from 'lane-shared/config';
import { M } from 'components/typography';
import { Icon } from 'design-system-web';

function useCanAccessReporting({
  channel,
  user,
}: {
  channel?: Channel;
  user?: UserType;
}) {
  const canAccessReporting =
    user?.isSuperUser ||
    hasPermission(
      user?.roles,
      [PERMISSION_KEYS.PERMISSION_ANALYTICS_WORK_ORDERS],
      channel?._id,
      false
    );

  return canAccessReporting;
}

function getReportDisplayName(
  reportName: string,
  t: (key: string) => string
): string {
  const reportNameMap: Record<string, string> = {
    'service-requests-summary': t(
      'web.admin.workOrder.reporting.serviceRequestsSummary'
    ),
    'target-times-report': t('web.admin.workOrder.reporting.targetTimesReport'),
    'preventive-maintenance-tasks-summary': t(
      'web.admin.workOrder.reporting.pmTasksSummary'
    ),
    'service-request-response-time': t(
      'web.admin.workOrder.reporting.serviceRequestResponseTime'
    ),
    'service-request-acceptance-time': t(
      'web.admin.workOrder.reporting.serviceRequestAcceptanceTime'
    ),
    'service-request-completion-time': t(
      'web.admin.workOrder.reporting.serviceRequestCompletionTime'
    ),
    'service-request-tenant-survey-summary': t(
      'web.admin.workOrder.reporting.serviceRequestTenantSurveySummary'
    ),
    'preventive-maintenance-time-evaluation-by-task': t(
      'web.admin.workOrder.reporting.preventiveMaintenanceTimeEvaluationByTask'
    ),
    'preventive-maintenance-task-performance-by-property': t(
      'web.admin.workOrder.reporting.preventiveMaintenanceTaskPerformanceByProperty'
    ),
    'preventive-maintenance-task-performance-by-assigned-user': t(
      'web.admin.workOrder.reporting.preventiveMaintenanceTaskPerformanceByAssignedUser'
    ),
    'preventive-maintenance-task-performance-by-assigned-team': t(
      'web.admin.workOrder.reporting.preventiveMaintenanceTaskPerformanceByAssignedTeam'
    ),
    'preventive-maintenance-task-performance-by-schedule': t(
      'web.admin.workOrder.reporting.preventiveMaintenanceTaskPerformanceBySchedule'
    ),
    'billing-export-report': t(
      'web.admin.workOrder.reporting.billingExportReport'
    ),
    'fin-charge': t('web.admin.workOrder.reporting.finCharge'),
    'fin-journal': t('web.admin.workOrder.reporting.finJournal'),
  };

  return reportNameMap[reportName] || reportName;
}

export function Report() {
  const { reportName } = useParams<{ reportName: string }>();
  const { t } = useTranslation();
  const [showAlert, setShowAlert] = React.useState(true);

  const { channel } = useChannelAdminContext();
  const { user } = useUserDataContext();
  const canAccessReporting = useCanAccessReporting({
    channel,
    user: user ?? undefined,
  });

  const { fetchEmbedURLs, embedURLs, loading: isLoading, error } = useReport();

  useEffect(() => {
    const getReportUrls = async (
      channelId: string,
      channelName: string,
      reportName: string
    ) => {
      const reportIds = getReportIds(reportName);
      const timeZone = getTimeZone();

      await fetchEmbedURLs(
        reportIds,
        {
          _id: channelId,
          name: channelName,
        },
        timeZone
      );
    };

    if (channel?._id && channel?.name && reportName) {
      getReportUrls(channel._id, channel.name, reportName);
    }
  }, [reportName]);

  useEffect(() => {
    if (error) {
      window.Toast.show(
        t('web.admin.workOrder.reporting.report.getReportError')
      );
    }
  }, [error, t]);

  if (error) {
    return (
      <div className={styles.container} data-test="work-order-report-error">
        <div />
      </div>
    );
  }

  if (!canAccessReporting) {
    return (
      <div className={styles.permissionsContainer}>
        <h1>{t('web.admin.workOrder.reporting.notAuthorized')}</h1>
      </div>
    );
  }

  return (
    <AdminPage className={styles.container}>
      <BreadCrumbs
        links={[
          {
            label: t('web.admin.workOrder.reporting.title'),
            url: routes.channelAdminWorkOrdersReporting.replace(
              ':id',
              channel?.slug!
            ),
          },
          { label: getReportDisplayName(reportName!, t) },
        ]}
      />
      <div className={styles.reportContainer}>
        {isLoading ? (
          <div className={styles.loaderContainer}>
            <Loading />
          </div>
        ) : (
          <div
            className={`${styles.iframeContainer} ${embedURLs.length === 1 ? styles.singleReport : ''}`}
          >
            {showAlert && (
              <Alert type="info" className={styles.alertContent}>
                <M className={styles.alertInfo}>
                  {t('web.admin.workOrder.reporting.realtimeDataDisclaimer')}
                </M>
                <Icon
                  name="times"
                  size="medium"
                  onClick={() => setShowAlert(false)}
                  className={styles.closeIcon}
                />
              </Alert>
            )}
            {embedURLs.map((embedResult, index) => (
              <iframe
                key={embedResult.reportId}
                id={`embed-work-order-report-iframe-${index}`}
                data-test={`embed-work-order-report-iframe-test-id-${index}`}
                className={styles.iFrame}
                src={embedResult.embedURL}
                title={`${reportName}-${embedResult.reportId}`}
              />
            ))}
          </div>
        )}
      </div>
    </AdminPage>
  );
}
